import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  LinearProgress,
  Box,
  Alert,
  Chip,
} from '@mui/material';
import {
  AccessTime,
  Security,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/store/hooks';
import { logout } from '@/store/thunks/authThunks';

interface SessionTimeoutWarningProps {
  /** Session timeout duration in minutes */
  sessionTimeout?: number;
  /** Warning shown when this many minutes remain */
  warningThreshold?: number;
  /** Callback when session is extended */
  onExtendSession?: () => void;
  /** Callback when user is logged out due to timeout */
  onTimeout?: () => void;
}

export const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  sessionTimeout = 30, // 30 minutes default
  warningThreshold = 5, // Show warning with 5 minutes left
  onExtendSession,
  onTimeout
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Track user activity
  useEffect(() => {
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const resetTimer = () => {
      setLastActivity(Date.now());
      setShowWarning(false);
      setTimeRemaining(null);
    };

    // Add event listeners for user activity
    activityEvents.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });

    return () => {
      // Cleanup event listeners
      activityEvents.forEach(event => {
        document.removeEventListener(event, resetTimer, true);
      });
    };
  }, []);

  // Session timeout logic
  useEffect(() => {
    const checkSessionTimeout = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;
      const sessionDurationMs = sessionTimeout * 60 * 1000;
      const warningThresholdMs = warningThreshold * 60 * 1000;
      const remaining = sessionDurationMs - timeSinceActivity;

      if (remaining <= 0) {
        // Session expired
        handleSessionExpired();
      } else if (remaining <= warningThresholdMs && !showWarning) {
        // Show warning
        setShowWarning(true);
        setTimeRemaining(Math.ceil(remaining / 1000)); // Convert to seconds
      } else if (remaining > warningThresholdMs) {
        // Reset warning state
        setShowWarning(false);
        setTimeRemaining(null);
      }
    };

    const interval = setInterval(checkSessionTimeout, 1000); // Check every second

    return () => clearInterval(interval);
  }, [lastActivity, sessionTimeout, warningThreshold, showWarning]);

  // Countdown timer for warning dialog
  useEffect(() => {
    if (showWarning && timeRemaining !== null) {
      if (timeRemaining <= 0) {
        handleSessionExpired();
        return;
      }

      const timer = setTimeout(() => {
        setTimeRemaining(prev => (prev !== null ? prev - 1 : null));
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [showWarning, timeRemaining]);

  const handleSessionExpired = async () => {
    setShowWarning(false);
    setTimeRemaining(null);
    
    try {
      await dispatch(logout());
      onTimeout?.();
      navigate('/auth/login', { 
        replace: true,
        state: { message: 'Your session has expired. Please log in again.' }
      });
    } catch (error) {
      console.error('Error during session timeout logout:', error);
    }
  };

  const handleExtendSession = () => {
    setLastActivity(Date.now());
    setShowWarning(false);
    setTimeRemaining(null);
    onExtendSession?.();
  };

  const handleLogoutNow = async () => {
    try {
      await dispatch(logout());
      navigate('/auth/login', { replace: true });
    } catch (error) {
      console.error('Error during manual logout:', error);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getProgressValue = () => {
    if (timeRemaining === null) return 0;
    const warningDurationSeconds = warningThreshold * 60;
    return ((warningDurationSeconds - timeRemaining) / warningDurationSeconds) * 100;
  };

  if (!showWarning || timeRemaining === null) {
    return null;
  }

  return (
    <Dialog
      open={showWarning}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          border: '2px solid',
          borderColor: 'warning.main',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        bgcolor: 'warning.main',
        color: 'warning.contrastText'
      }}>
        <Warning />
        Session Expiring Soon
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Your session will expire due to inactivity. You will be automatically logged out for security reasons.
          </Typography>
        </Alert>

        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h3" color="warning.main" sx={{ fontWeight: 600, mb: 1 }}>
            {formatTime(timeRemaining)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Time remaining
          </Typography>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 3 }}>
          <LinearProgress
            variant="determinate"
            value={getProgressValue()}
            color="warning"
            sx={{ 
              height: 8, 
              borderRadius: 4,
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
              }
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
          <Chip
            icon={<Security />}
            label="Security Feature"
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            icon={<AccessTime />}
            label={`${sessionTimeout} min session`}
            size="small"
            variant="outlined"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          Click "Stay Logged In" to continue your session, or "Logout" to securely end your session now.
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleLogoutNow}
          color="inherit"
          variant="outlined"
        >
          Logout Now
        </Button>
        <Button
          onClick={handleExtendSession}
          color="primary"
          variant="contained"
          autoFocus
        >
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};